"use client";

import { useTranslations } from "next-intl";
import { useEffect } from "react";
import type { KlaroConfig, KlaroService } from "@/types/klaro";
import { loadServicesBasedOnConsent } from "@/utils/consent";
import { deInitializeAnalytics } from "@/lib/google-analytics";

export function useKlaroConfig(locale: string): KlaroConfig {
  const t = useTranslations("cookies");

  return {
    testing: false,
    styling: {
      theme: ["light", "bottom"],
    },
    noNotice: false,
    htmlTexts: true,
    cookieExpiresAfterDays: 365,
    default: false,
    mustConsent: false,
    acceptAll: true,
    hideDeclineAll: false,
    translations: {
      [locale]: {
        privacyPolicyUrl: t("privacyPolicyUrl"),
        privacyPolicy: {
          name: t("privacyPolicy.name"),
          text: `${t("privacyPolicy.text")} <a href="${t(
            "privacyPolicyUrl"
          )}">${t("privacyPolicy.name")}</a>.`,
        },
        consentNotice: {
          description: t("consentNotice.description"),
          learnMore: t("consentNotice.learnMore"),
        },
        purposeItem: {
          service: t("purposeItem.service"),
          services: t("purposeItem.services"),
        },
        purposes: {
          essential: t("purposes.essential"),
          analytics: t("purposes.analytics"),
          marketing: t("purposes.marketing"),
          authentication: t("purposes.authentication"),
        },
        ok: t("actions.ok"),
        acceptAll: t("actions.acceptAll"),
        acceptSelected: t("actions.acceptSelected"),
        decline: t("actions.decline"),
        service: {
          required: {
            title: t("services.required.title"),
            description: t("services.required.description"),
          },
          analytics: {
            description: t("services.analytics.description"),
          },
          marketing: {
            description: t("services.marketing.description"),
          },
          authentication: {
            description: t("services.authentication.description"),
          },
        },
      },
    },
    services: [
      {
        name: "supabase-essential",
        title: "Supabase Essential",
        purposes: ["essential"],
        required: true,
        default: true,
        cookies: [["sb-[a-zA-Z0-9-_.]+", "/", "aiaccountant.biz"]],
        description: t("services.required.description"),
      },
      {
        name: "openai",
        title: "OpenAI Services",
        purposes: ["essential"],
        required: true,
        default: true,
        cookies: [["openai-session", "/", "aiaccountant.biz"]],
        description: t("services.required.description"),
      },
      {
        name: "stripe",
        title: "Stripe Payments",
        purposes: ["essential"],
        required: true,
        default: true,
        cookies: [["stripe", "/", "aiaccountant.biz"]],
        description: t("services.required.description"),
      },
      {
        name: "google-auth",
        title: "Google Authentication",
        purposes: ["authentication"],
        required: false,
        default: true,
        cookies: [["google_auth", "/", "aiaccountant.biz"]],
        callback: function (consent: boolean, service: KlaroService) {
          if (consent === true) {
            loadServicesBasedOnConsent();
          }
        },
        description: t("services.authentication.description"),
      },
      {
        name: "google-analytics",
        title: "Google Analytics",
        purposes: ["analytics"],
        required: false,
        default: false,
        cookies: [
          ["_ga", "/", "aiaccountant.biz"],
          ["_gat", "/", "aiaccountant.biz"],
          ["_gid", "/", "aiaccountant.biz"],
        ],
        callback: function (consent: boolean, service: KlaroService) {
          if (consent === true) {
            loadServicesBasedOnConsent();
          } else {
            deInitializeAnalytics();
          }
        },
        description: t("services.analytics.description"),
      },
      {
        name: "facebook-pixel",
        title: "Facebook Pixel",
        purposes: ["marketing"],
        required: false,
        default: false,
        cookies: [
          ["_fbp", "/", "aiaccountant.biz"],
          ["fr", "/", "facebook.com"],
        ],
        callback: function (consent: boolean, service: KlaroService) {
          if (consent === true) {
            if (window.fbq) {
              window.fbq("consent", "grant");
            }
          } else {
            if (window.fbq) {
              window.fbq("consent", "revoke");
            }
          }
        },
        description: t("services.marketing.description"),
      },
    ],
  };
}

export function KlaroProvider({ locale }: { locale: string }) {
  const config = useKlaroConfig(locale);

  useEffect(() => {
    let mounted = true;

    const initKlaro = async () => {
      if (!mounted) return;

      window.klaroConfig = config;
      if (window.klaro) {
        if (typeof window.klaro === "function") {
          window.klaro(config);
        } else if (window.klaro.render) {
          window.klaro.render(config);
        } else if (window.klaro.setup) {
          window.klaro.setup(config);
          window.klaro.show();
        } else {
          await loadKlaroFromCDN();
        }
        return;
      }

      await loadKlaroFromCDN();
    };

    const loadKlaroFromCDN = () => {
      const script = document.createElement("script");
      script.src = "https://cdn.kiprotect.com/klaro/v0.7/klaro.js";
      script.async = true;

      return new Promise((resolve, reject) => {
        script.onload = () => {
          if (!mounted) {
            reject(new Error("Component unmounted"));
            return;
          }

          setTimeout(() => {
            try {
              if (typeof window.klaro === "function") {
                window.klaro(config);
              } else if (window.klaro?.render) {
                window.klaro.render(config);
              } else if (window.klaro?.setup) {
                window.klaro.setup(config);
                window.klaro.show();
              } else {
                throw new Error("No valid Klaro initialization method found");
              }
              resolve(undefined);
            } catch (e) {
              reject(new Error(`Klaro initialization failed: ${e}`));
            }
          }, 100);
        };

        script.onerror = () => {
          reject(new Error("Failed to load Klaro script"));
        };

        document.head.appendChild(script);
      });
    };

    initKlaro().catch((error) => {
      if (mounted) {
        console.error("Klaro initialization error:", error);
      }
    });

    return () => {
      mounted = false;
      const script = document.querySelector('script[src*="klaro.js"]');
      if (script) {
        script.remove();
      }
    };
  }, [config]);

  return null;
}
